import { BarChart3 } from 'lucide-react';
import type { DashboardRoleCard, DashboardRoleFilter } from '../../../types/admin-dashboard';

type Props = {
  cards: DashboardRoleCard[];
  selectedRole: DashboardRoleFilter;
  onSelectRole: (role: DashboardRoleFilter) => void;
  onReports: () => void;
  actorRole?: 'ADMIN' | 'SUPER_MASTER' | 'MASTER' | 'DISTRIBUTOR';
};

const labels: Record<DashboardRoleCard['role'], string> = { SUPER_MASTER: 'Super Master', MASTER: 'Master', DISTRIBUTOR: 'Distributor', USER: 'User' };
const accents: Record<DashboardRoleFilter, string> = { ALL: 'bg-indigo-400', SUPER_MASTER: 'bg-amber-400', MASTER: 'bg-sky-400', DISTRIBUTOR: 'bg-violet-400', USER: 'bg-emerald-400' };
const roleOrder: DashboardRoleFilter[] = ['ALL', 'SUPER_MASTER', 'MASTER', 'DISTRIBUTOR', 'USER'];

export function AdminReportsButton({ onReports }: { onReports: () => void }) {
  return (
    <button type="button" onClick={onReports} className="crystal-btn crystal-emerald relative min-h-[48px] overflow-hidden rounded-xl px-3 py-2 text-left text-[#DCEAE5] outline-none [-webkit-tap-highlight-color:transparent] focus:outline-none" aria-label="Open reports">
      <span className="flex h-full items-center gap-2"><BarChart3 size={17} aria-hidden="true" /><span className="text-[11px] font-bold uppercase tracking-[0.03em] sm:text-xs">Reports</span></span>
    </button>
  );
}

export default function AdminRoleCards({ cards, selectedRole, onSelectRole, onReports, actorRole = 'ADMIN' }: Props) {
  const visibleRoles = actorRole === 'SUPER_MASTER'
    ? ['ALL', 'MASTER', 'DISTRIBUTOR', 'USER'] as DashboardRoleFilter[]
    : actorRole === 'MASTER'
      ? ['ALL', 'DISTRIBUTOR', 'USER'] as DashboardRoleFilter[]
      : actorRole === 'DISTRIBUTOR'
        ? [] as DashboardRoleFilter[]
        : roleOrder;
  const total = cards.reduce((sum, card) => sum + card.count, 0);
  const descendantTotal = cards.reduce((sum, card) => sum + (card.descendantCount ?? card.count), 0);
  const byRole = new Map<DashboardRoleFilter, { role: DashboardRoleFilter; label: string; count: number; descendantCount: number }>([
    ['ALL', { role: 'ALL', label: 'All', count: total, descendantCount: descendantTotal }],
    ...cards.map((card) => [card.role, { role: card.role, label: labels[card.role], count: card.count, descendantCount: card.descendantCount ?? card.count }] as const),
  ]);
  const items = visibleRoles.map((role) => byRole.get(role)).filter(Boolean) as Array<{ role: DashboardRoleFilter; label: string; count: number; descendantCount: number }>;
  if (!items.length) return null;

  return (
    <section className={`grid gap-2 ${actorRole === 'SUPER_MASTER' ? 'grid-cols-4' : 'grid-cols-3 sm:grid-cols-6'}`} aria-label="Account role filters">
      {items.map((item) => {
        const selected = selectedRole === item.role;
        return (
          <button
            key={item.role}
            type="button"
            onClick={() => onSelectRole(item.role)}
            className={`crystal-btn crystal-smoky relative min-h-[48px] overflow-hidden rounded-xl px-2 py-1.5 text-left text-white outline-none [-webkit-tap-highlight-color:transparent] focus:outline-none ${selected ? 'brightness-110' : ''}`}
            aria-label={`${item.label}, ${item.count} accounts`}
          >
            <span className={`absolute inset-y-0 left-0 w-[3px] ${accents[item.role]}`} />
            <span className={`absolute right-2 top-1.5 h-1.5 w-1.5 rounded-full ${accents[item.role]}`} />
            <span className="block truncate pl-1 text-[9px] font-bold uppercase tracking-[0.03em] text-slate-300 sm:text-[10px]">{item.label}</span>
            <span className="numeric-data mt-0.5 block pl-1 text-[15px] font-bold leading-none text-white sm:text-base">{item.count.toLocaleString('en-US')}</span>
            {actorRole !== 'ADMIN' ? <span className="numeric-data mt-1 block pl-1 text-[9px] font-medium leading-none text-slate-300">Total {item.descendantCount.toLocaleString('en-US')}</span> : null}
          </button>
        );
      })}
      {actorRole === 'ADMIN' ? <AdminReportsButton onReports={onReports} /> : null}
    </section>
  );
}
